import type { Envelope } from '@/types';

interface EnvelopeCardProps {
    envelope: Envelope;
}

const EnvelopeCard = ({ envelope }: EnvelopeCardProps) => {
    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        });
    };

    // Flower decorations for each envelope color
    const flowerOptions = {
        cream: { emoji: '🌾', color: '#D4A574' },
        rose: { emoji: '🌸', color: '#F5A9B8' },
        sage: { emoji: '🌿', color: '#7A9B7A' },
        lavender: { emoji: '💜', color: '#B8A5D6' },
        peach: { emoji: '🌻', color: '#FFB347' },
        mint: { emoji: '🍃', color: '#98D9C2' },
        burgundy: { emoji: '🥀', color: '#D4A5A5' },
    };

    // Ultra-realistic envelope designs
    const envelopeDesigns = {
        cream: {
            bg: '#E8DBC5',
            flap: '#D4C4A8',
            twine: '#8B7355',
            seal: '#A0826D',
            shadow: 'rgba(107, 75, 58, 0.3)',
        },
        rose: {
            bg: '#F5D5D8',
            flap: '#E8C4C8',
            twine: '#C09A8A',
            seal: '#D4A5A5',
            shadow: 'rgba(212, 165, 165, 0.3)',
        },
        sage: {
            bg: '#C8D5C8',
            flap: '#B8C5B8',
            twine: '#7A8F7A',
            seal: '#8FAF8F',
            shadow: 'rgba(122, 143, 122, 0.3)',
        },
        lavender: {
            bg: '#E6D9F5',
            flap: '#D8C9E8',
            twine: '#9B87C4',
            seal: '#B8A5D6',
            shadow: 'rgba(184, 165, 214, 0.3)',
        },
        peach: {
            bg: '#FFDFC4',
            flap: '#F5D0B0',
            twine: '#D4A574',
            seal: '#E8A87C',
            shadow: 'rgba(232, 168, 124, 0.3)',
        },
        mint: {
            bg: '#D0E8E0',
            flap: '#C0D8D0',
            twine: '#7BA898',
            seal: '#98D9C2',
            shadow: 'rgba(152, 217, 194, 0.3)',
        },
        burgundy: {
            bg: '#8B4049',
            flap: '#7A3540',
            twine: '#D4AF9A',
            seal: '#C89B85',
            shadow: 'rgba(139, 64, 73, 0.4)',
        },
    };

    const design = envelopeDesigns[envelope.envelope_design || 'cream'];
    const flower = flowerOptions[envelope.envelope_design || 'cream'];
    const isDark = envelope.envelope_design === 'burgundy';

    return (
        <div className="relative h-full perspective-envelope group">
            {/* Main envelope body with realistic depth */}
            <div 
                className="relative w-full h-full rounded-lg overflow-visible shadow-xl hover:shadow-2xl transition-all duration-700 transform hover:-translate-y-3 hover:rotate-1"
                style={{
                    backgroundColor: design.bg,
                    boxShadow: `0 8px 16px ${design.shadow}, 0 2px 4px rgba(0,0,0,0.1)`,
                }}
            >
                {/* Vintage paper texture with grain */}
                <div 
                    className="absolute inset-0 rounded-lg opacity-50"
                    style={{
                        backgroundImage: `
                            repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.02) 2px, rgba(0,0,0,0.02) 4px),
                            repeating-linear-gradient(90deg, transparent, transparent 2px, rgba(0,0,0,0.02) 2px, rgba(0,0,0,0.02) 4px)
                        `,
                    }}
                />

                {/* Envelope flap with realistic fold */}
                <div 
                    className="absolute top-0 left-0 right-0 h-28 overflow-visible z-20"
                    style={{
                        clipPath: 'polygon(0 0, 50% 100%, 100% 0)',
                        transformStyle: 'preserve-3d',
                    }}
                >
                    <div 
                        className="w-full h-full relative"
                        style={{
                            background: `linear-gradient(180deg, ${design.flap} 0%, ${design.bg} 100%)`,
                            boxShadow: `inset 0 -3px 6px rgba(0,0,0,0.15)`,
                        }}
                    >
                        {/* Flap border lines */}
                        <div 
                            className="absolute top-0 left-0 w-1/2 h-full border-r"
                            style={{ 
                                borderColor: 'rgba(0,0,0,0.08)',
                                transform: 'skewY(-30deg)',
                                transformOrigin: 'top right',
                            }}
                        />
                    </div>
                </div>

                {/* Jute twine wrapping - more realistic with texture */}
                <div className="absolute inset-0 pointer-events-none z-10">
                    {/* Horizontal twine */}
                    <div 
                        className="absolute left-0 right-0"
                        style={{ 
                            top: '50%',
                            height: '4px',
                            background: `repeating-linear-gradient(
                                90deg,
                                ${design.twine} 0px,
                                ${design.twine}DD 2px,
                                ${design.twine}AA 4px
                            )`,
                            transform: 'translateY(-50%)',
                            boxShadow: `0 2px 3px rgba(0,0,0,0.2), inset 0 1px 1px rgba(255,255,255,0.3)`,
                        }}
                    />
                    {/* Vertical twine */}
                    <div 
                        className="absolute top-0 bottom-0"
                        style={{ 
                            left: '50%',
                            width: '4px',
                            background: `repeating-linear-gradient(
                                180deg,
                                ${design.twine} 0px,
                                ${design.twine}DD 2px,
                                ${design.twine}AA 4px
                            )`,
                            transform: 'translateX(-50%)',
                            boxShadow: `0 2px 3px rgba(0,0,0,0.2), inset 0 1px 1px rgba(255,255,255,0.3)`,
                        }}
                    />
                    
                    {/* Twine knot at center */}
                    <div 
                        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-6 h-6 rounded-full"
                        style={{
                            background: `radial-gradient(circle, ${design.twine}FF, ${design.twine}CC)`,
                            boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                        }}
                    />
                </div>

                {/* Content area */}
                <div className="relative px-6 pt-32 pb-8 h-full flex flex-col justify-between z-5">
                    
                    {/* Handwritten message on flap */}
                    <div 
                        className="absolute top-3 left-1/2 -translate-x-1/2 font-dancing text-base opacity-60 z-30"
                        style={{ color: isDark ? '#F5E6D3' : '#6B5744' }}
                    >
                        for you ♥
                    </div>

                    {/* Title and date */}
                    <div className="text-center space-y-3 flex-grow flex flex-col justify-center">
                        <h3 
                            className="text-xl md:text-2xl font-playfair font-bold leading-tight px-2"
                            style={{ color: isDark ? '#F5E6D3' : '#3D2817' }}
                        >
                            {envelope.title}
                        </h3>
                        
                        <div 
                            className="text-xs font-cormorant italic"
                            style={{ color: isDark ? '#E8DED0' : '#6B4B3A' }}
                        >
                            {formatDate(envelope.created_at)}
                        </div>
                    </div>

                    {/* Bottom decorative pattern */}
                    <div className="mt-4">
                        <div className="flex justify-center gap-1 opacity-20 text-xs">
                            {[...Array(5)].map((_, i) => (
                                <span key={i} style={{ color: design.twine }}>❤</span>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Realistic wax seal with 3D effect */}
                <div 
                    className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-20 h-20 rounded-full flex items-center justify-center z-30 group-hover:scale-110 transition-transform duration-500"
                    style={{
                        background: `radial-gradient(circle at 35% 35%, 
                            ${design.seal}FF 0%,
                            ${design.seal}EE 30%,
                            ${design.seal}DD 60%,
                            ${design.seal}CC 100%
                        )`,
                        boxShadow: `
                            0 6px 12px ${design.shadow},
                            inset -2px -2px 4px rgba(0,0,0,0.3),
                            inset 2px 2px 4px rgba(255,255,255,0.4)
                        `,
                    }}
                >
                    {/* Wax drips */}
                    <div 
                        className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-8 h-4"
                        style={{
                            background: design.seal,
                            clipPath: 'polygon(30% 0, 50% 100%, 70% 0)',
                            opacity: 0.9,
                        }}
                    />
                    
                    {/* Seal embossed pattern */}
                    <div className="absolute inset-2 rounded-full border-2 border-white/20" />
                    
                    {/* Heart symbol */}
                    <span 
                        className="text-3xl relative z-10 font-serif"
                        style={{ 
                            color: isDark ? '#F5E6D3' : 'rgba(255,255,255,0.95)',
                            textShadow: '0 2px 3px rgba(0,0,0,0.3)',
                        }}
                    >
                        ♥
                    </span>
                </div>

                {/* Dried flowers tied to twine */}
                <div 
                    className="absolute z-20 flex items-center gap-1"
                    style={{
                        top: 'calc(50% - 32px)',
                        left: 'calc(50% + 40px)',
                    }}
                >
                    <div className="flex flex-col gap-1">
                        <span 
                            className="text-2xl animate-swing"
                            style={{ 
                                filter: 'drop-shadow(0 2px 3px rgba(0,0,0,0.3))',
                                transformOrigin: 'top center',
                            }}
                        >
                            {flower.emoji}
                        </span>
                        {/* Thin thread holding flower */}
                        <div 
                            className="absolute top-0 left-1/2 w-px h-8 -translate-x-1/2 -translate-y-full"
                            style={{ 
                                background: design.twine,
                                opacity: 0.6,
                            }}
                        />
                    </div>
                </div>

                {/* Small decorative flowers on bottom corners */}
                <div 
                    className="absolute bottom-4 left-4 text-lg opacity-50"
                    style={{ 
                        filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.2))',
                    }}
                >
                    {flower.emoji}
                </div>
                <div 
                    className="absolute bottom-4 right-4 text-lg opacity-50"
                    style={{ 
                        filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.2))',
                        transform: 'scaleX(-1)',
                    }}
                >
                    {flower.emoji}
                </div>

                {/* Vintage postage stamp */}
                <div 
                    className="absolute top-4 right-4 w-14 h-16 bg-white/80 border-2 border-dashed flex flex-col items-center justify-center text-[9px] font-serif z-20"
                    style={{ 
                        borderColor: design.twine,
                        color: design.twine,
                        transform: 'rotate(8deg)',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                        backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 2px, rgba(0,0,0,0.03) 2px, rgba(0,0,0,0.03) 4px)',
                    }}
                >
                    <div className="font-bold">LOVE</div>
                    <div className="text-lg my-1">{flower.emoji}</div>
                    <div className="font-bold">LETTER</div>
                    
                    {/* Stamp perforations */}
                    <div className="absolute -top-1 left-1 right-1 flex justify-between">
                        {[...Array(5)].map((_, i) => (
                            <div key={i} className="w-1 h-1 rounded-full bg-white" />
                        ))}
                    </div>
                    <div className="absolute -bottom-1 left-1 right-1 flex justify-between">
                        {[...Array(5)].map((_, i) => (
                            <div key={i} className="w-1 h-1 rounded-full bg-white" />
                        ))}
                    </div>
                </div>

                {/* Vintage postmark */}
                <div 
                    className="absolute top-6 left-4 w-16 h-16 rounded-full border-2 opacity-30 flex items-center justify-center z-20"
                    style={{ borderColor: design.twine }}
                >
                    <div className="text-center text-[8px] font-serif leading-tight" style={{ color: design.twine }}>
                        <div className="font-bold">SEALED</div>
                        <div className="text-xs my-0.5">♥</div>
                        <div className="font-bold">WITH LOVE</div>
                    </div>
                    
                    {/* Postmark wavy cancellation lines */}
                    <svg className="absolute right-0 w-8 h-6" style={{ opacity: 0.3 }}>
                        <path 
                            d="M0,4 Q2,2 4,4 T8,4 M0,8 Q2,6 4,8 T8,8 M0,12 Q2,10 4,12 T8,12"
                            stroke={design.twine}
                            strokeWidth="0.5"
                            fill="none"
                        />
                    </svg>
                </div>

                {/* Lace trim on edges */}
                <div 
                    className="absolute top-0 left-0 right-0 h-1 opacity-20"
                    style={{
                        background: `repeating-linear-gradient(90deg, ${design.twine}, ${design.twine} 4px, transparent 4px, transparent 8px)`,
                    }}
                />
                <div 
                    className="absolute bottom-0 left-0 right-0 h-1 opacity-20"
                    style={{
                        background: `repeating-linear-gradient(90deg, ${design.twine}, ${design.twine} 4px, transparent 4px, transparent 8px)`,
                    }}
                />
            </div>
        </div>
    );
};

export default EnvelopeCard;
